import { useState, useEffect, useRef, useCallback } from "react";
import { axiosInstance } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MessageCircle, Send, X, Users, Bell, Smile, Trash2, MoreVertical, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import EmojiPicker from 'emoji-picker-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const boxEmojis = ['📦', '🏪', '💼', '🛒', '🏬', '🏢', '🏭', '🏗️'];
const masterEmojis = ['👨‍💼', '👩‍💼', '👔', '🎩', '👑', '⭐', '💎', '🔱'];

export default function ChatWidget({ user, selectedBox, embedded = false, inHeader = false }) {
  const [isOpen, setIsOpen] = useState(embedded ? true : false);
  const [messages, setMessages] = useState([]);
  const [groupMessages, setGroupMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [activeTab, setActiveTab] = useState("direct");
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [deleteConversationDialogOpen, setDeleteConversationDialogOpen] = useState(false);
  const [availableBoxes, setAvailableBoxes] = useState([]);
  const [masterUsers, setMasterUsers] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null); // {type:'box'|'user', id, name, emoji}
  const [groupMasterId, setGroupMasterId] = useState(null); // solo super master elige grupo

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const isMountedRef = useRef(true);
  const emojiPickerRef = useRef(null);
  const prevUnreadRef = useRef(0);
  const isOpenRef = useRef(isOpen);

  const isMaster = user?.role === "master";
  const isSuper = !!user?.is_super_master;

  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; setShowEmojiPicker(false); };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showEmojiPicker]);

  // Fetch all recipients (all boxes + all masters) — regardless of ownership
  useEffect(() => {
    const fetchRecipients = async () => {
      try {
        const r = await axiosInstance.get('/chat/recipients');
        const boxes = (r.data.boxes || []).map((b, i) => ({
          type: 'box', id: b.id, name: b.name, emoji: boxEmojis[i % boxEmojis.length],
        }));
        const myBoxId = user?.role === 'cajero' ? user?.assigned_box_id : null;
        setAvailableBoxes(boxes.filter((b) => b.id !== myBoxId));

        const users = (r.data.users || [])
          .filter((u) => u.username !== user?.username)
          .map((u, i) => ({
            type: 'user', id: u.id, name: u.username, emoji: u.is_super_master ? '👑' : masterEmojis[i % masterEmojis.length], is_super_master: u.is_super_master,
          }));
        setMasterUsers(users);

        // Grupo por defecto para super master
        if (isSuper && !groupMasterId) {
          const me = (r.data.users || []).find((u) => u.username === user?.username);
          setGroupMasterId(me ? me.id : ((r.data.users || [])[0]?.id || null));
        }

        if (selectedBox && !selectedRecipient) {
          const match = boxes.find((b) => b.id === selectedBox.id && b.id !== myBoxId);
          if (match) setSelectedRecipient(match);
        }
      } catch (e) {
        console.error("Error fetching recipients:", e);
      }
    };
    if (isOpen) fetchRecipients();
  }, [isOpen]);

  const groupQuery = useCallback(() => {
    return isSuper && groupMasterId ? `&group_master_id=${groupMasterId}` : '';
  }, [isSuper, groupMasterId]);

  const markRead = useCallback(async () => {
    try {
      if (activeTab === 'group') {
        await axiosInstance.patch(`/chat/mark-read?is_group=true${groupQuery()}`);
      } else if (selectedRecipient) {
        const key = selectedRecipient.type === 'box' ? `to_box_id=${selectedRecipient.id}` : `to_user_id=${selectedRecipient.id}`;
        await axiosInstance.patch(`/chat/mark-read?is_group=false&${key}`);
      }
    } catch (e) { /* noop */ }
  }, [activeTab, selectedRecipient, groupQuery]);

  const fetchMessages = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      if (activeTab === "direct" && selectedRecipient) {
        const key = selectedRecipient.type === 'box' ? `to_box_id=${selectedRecipient.id}` : `to_user_id=${selectedRecipient.id}`;
        const r = await axiosInstance.get(`/chat/messages?is_group=false&${key}`);
        if (isMountedRef.current) setMessages(r.data);
      } else if (activeTab === "group") {
        const r = await axiosInstance.get(`/chat/messages?is_group=true${groupQuery()}`);
        if (isMountedRef.current) setGroupMessages(r.data);
      }
      markRead();
    } catch (e) {
      console.error("Error fetching messages:", e);
    }
  }, [activeTab, selectedRecipient, groupQuery, markRead]);

  // Global unread poll (every 3s) — works on any screen, even when chat closed
  const pollUnread = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/chat/unread-summary');
      const total = r.data?.total || 0;
      if (isMountedRef.current) setUnreadTotal(total);
      if (!isOpenRef.current && total > prevUnreadRef.current) {
        const diff = total - prevUnreadRef.current;
        toast.info(`Tienes ${diff} mensaje${diff > 1 ? 's' : ''} nuevo${diff > 1 ? 's' : ''} en el chat`, {
          icon: <Bell className="w-4 h-4" />,
        });
      }
      prevUnreadRef.current = total;
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    pollUnread();
    const interval = setInterval(pollUnread, 3000);
    return () => clearInterval(interval);
  }, [pollUnread]);

  // Poll messages while open (every 3s)
  useEffect(() => {
    if (!isOpen) return;
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [isOpen, fetchMessages]);

  useEffect(() => { scrollToBottom(); }, [messages, groupMessages]);

  const handleOpen = () => setIsOpen(true);

  const onEmojiClick = (emojiObject) => {
    if (!isMountedRef.current) return;
    setNewMessage((prev) => prev + emojiObject.emoji);
    setShowEmojiPicker(false);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await axiosInstance.delete(`/chat/message/${messageId}`);
      toast.success("Mensaje eliminado");
      fetchMessages();
      setDeleteDialogOpen(false);
      setMessageToDelete(null);
    } catch (e) {
      toast.error("Error al eliminar mensaje");
    }
  };

  const handleDeleteConversation = async () => {
    try {
      if (activeTab === "group") {
        await axiosInstance.delete(`/chat/conversation?is_group=true${groupQuery()}`);
      } else {
        if (!selectedRecipient) { toast.error("No hay conversación seleccionada"); return; }
        const key = selectedRecipient.type === 'box' ? `to_box_id=${selectedRecipient.id}` : `to_user_id=${selectedRecipient.id}`;
        await axiosInstance.delete(`/chat/conversation?is_group=false&${key}`);
      }
      toast.success("Conversación eliminada");
      fetchMessages();
      setDeleteConversationDialogOpen(false);
    } catch (e) {
      toast.error("Error al eliminar conversación");
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    if (activeTab === "direct" && !selectedRecipient) {
      toast.error("Selecciona un destinatario");
      return;
    }
    try {
      const isGroup = activeTab === "group";
      let query = `message=${encodeURIComponent(newMessage)}&is_group=${isGroup}`;
      if (isGroup) {
        query += groupQuery();
      } else if (selectedRecipient) {
        query += selectedRecipient.type === 'box' ? `&to_box_id=${selectedRecipient.id}` : `&to_user_id=${selectedRecipient.id}`;
      }
      await axiosInstance.post(`/chat/send?${query}`);
      setNewMessage("");
      fetchMessages();
    } catch (e) {
      toast.error("Error al enviar mensaje");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const groupLabel = () => {
    if (isSuper) {
      const m = masterUsers.find((u) => u.id === groupMasterId);
      if (groupMasterId === user?.id) return `Mi grupo (${user?.username})`;
      return m ? `Grupo de ${m.name}` : 'Selecciona un grupo';
    }
    return 'Grupo de tu equipo';
  };

  if (!isOpen && !embedded) {
    const buttonClass = inHeader
      ? "relative bg-blue-500 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300"
      : "fixed top-6 right-6 bg-blue-500 hover:bg-blue-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 z-50 relative";
    return (
      <button onClick={handleOpen} className={buttonClass} title="Chat Interno" data-testid="chat-open-button">
        <MessageCircle className={inHeader ? "w-5 h-5" : "w-6 h-6"} />
        {unreadTotal > 0 && (
          <span
            data-testid="chat-unread-badge"
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center animate-pulse"
          >
            {unreadTotal > 9 ? '9+' : unreadTotal}
          </span>
        )}
      </button>
    );
  }

  const containerClass = embedded
    ? "w-full h-[600px]"
    : inHeader
    ? "fixed top-20 right-6 w-96 h-[550px] z-50 shadow-2xl"
    : "fixed top-6 right-6 w-96 h-[550px] z-50 shadow-2xl";

  return (
    <div className={containerClass} data-testid="chat-panel">
      <Card className="h-full flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 bg-blue-500 text-white rounded-t-lg">
          <CardTitle className="text-lg font-semibold">Chat Interno</CardTitle>
          <div className="flex items-center gap-2">
            {isMaster && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="hover:bg-blue-600 text-white">
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => setDeleteConversationDialogOpen(true)}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar conversación
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {!embedded && (
              <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} className="hover:bg-blue-600 text-white" data-testid="chat-close-button">
                <X className="w-5 h-5" />
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col p-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
            <TabsList className="grid w-full grid-cols-2 rounded-none">
              <TabsTrigger value="direct" data-testid="chat-tab-direct">💬 1 a 1</TabsTrigger>
              <TabsTrigger value="group" data-testid="chat-tab-group">
                <Users className="w-4 h-4 mr-2" /> 👥 Grupal
              </TabsTrigger>
            </TabsList>

            {/* Recipient Selector for Direct Messages */}
            {activeTab === "direct" && (
              <div className="p-3 border-b border-slate-200 bg-slate-50">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="w-full justify-between text-left font-normal" data-testid="chat-recipient-trigger">
                      <span className="flex items-center gap-2">
                        {selectedRecipient ? (
                          <>
                            <span className="text-lg">{selectedRecipient.emoji}</span>
                            <span className="truncate">{selectedRecipient.name}</span>
                          </>
                        ) : (
                          <span className="text-slate-500">Seleccionar destinatario...</span>
                        )}
                      </span>
                      <ChevronDown className="w-4 h-4 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-80 max-h-96 overflow-y-auto" align="start">
                    {availableBoxes.length > 0 && (
                      <>
                        <DropdownMenuLabel className="text-xs text-slate-500 uppercase">📦 Cajas</DropdownMenuLabel>
                        {availableBoxes.map((box) => (
                          <DropdownMenuItem key={box.id} onClick={() => setSelectedRecipient(box)} className="gap-2" data-testid={`chat-recipient-box-${box.id}`}>
                            <span className="text-lg">{box.emoji}</span>
                            <span>{box.name}</span>
                            {selectedRecipient?.type === 'box' && selectedRecipient?.id === box.id && <span className="ml-auto text-blue-500">✓</span>}
                          </DropdownMenuItem>
                        ))}
                        {masterUsers.length > 0 && <DropdownMenuSeparator />}
                      </>
                    )}
                    {masterUsers.length > 0 && (
                      <>
                        <DropdownMenuLabel className="text-xs text-slate-500 uppercase">👔 Masters</DropdownMenuLabel>
                        {masterUsers.map((m) => (
                          <DropdownMenuItem key={m.id} onClick={() => setSelectedRecipient(m)} className="gap-2" data-testid={`chat-recipient-user-${m.id}`}>
                            <span className="text-lg">{m.emoji}</span>
                            <span>{m.name}</span>
                            {selectedRecipient?.type === 'user' && selectedRecipient?.id === m.id && <span className="ml-auto text-blue-500">✓</span>}
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                    {availableBoxes.length === 0 && masterUsers.length === 0 && (
                      <div className="p-4 text-center text-slate-500 text-sm">No hay destinatarios disponibles</div>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* Group selector (only super master) */}
            {activeTab === "group" && (
              <div className="p-3 border-b border-slate-200 bg-slate-50">
                {isSuper ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-full justify-between text-left font-normal" data-testid="chat-group-trigger">
                        <span className="flex items-center gap-2 truncate">👥 {groupLabel()}</span>
                        <ChevronDown className="w-4 h-4 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-80 max-h-96 overflow-y-auto" align="start">
                      <DropdownMenuLabel className="text-xs text-slate-500 uppercase">Grupos por Master</DropdownMenuLabel>
                      {user?.id && (
                        <DropdownMenuItem onClick={() => setGroupMasterId(user.id)} className="gap-2" data-testid={`chat-group-${user.id}`}>
                          <span className="text-lg">👑</span>
                          <span>Mi grupo ({user.username})</span>
                          {groupMasterId === user.id && <span className="ml-auto text-blue-500">✓</span>}
                        </DropdownMenuItem>
                      )}
                      {masterUsers.map((m) => (
                        <DropdownMenuItem key={m.id} onClick={() => setGroupMasterId(m.id)} className="gap-2" data-testid={`chat-group-${m.id}`}>
                          <span className="text-lg">{m.emoji}</span>
                          <span>Grupo de {m.name}</span>
                          {groupMasterId === m.id && <span className="ml-auto text-blue-500">✓</span>}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <div className="text-sm text-slate-600 flex items-center gap-2" data-testid="chat-group-label">
                    <Users className="w-4 h-4 text-green-600" /> {groupLabel()}
                  </div>
                )}
              </div>
            )}

            <TabsContent value="direct" className="flex-1 flex flex-col mt-0 h-0">
              <div className="h-full overflow-y-auto p-4 space-y-3 bg-slate-50 scrollbar-chat">
                {!selectedRecipient ? (
                  <div className="text-center text-slate-500 py-8">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">Selecciona un destinatario</p>
                    <p className="text-sm mt-1">Elige cualquier caja o master del sistema</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-slate-500 py-8">
                    <span className="text-4xl mb-3 block">{selectedRecipient.emoji}</span>
                    <p className="font-medium">No hay mensajes con {selectedRecipient.name}</p>
                    <p className="text-sm mt-1">Envía el primer mensaje para iniciar la conversación</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isMine = msg.from_user === user.username;
                    return (
                      <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'} group`}>
                        <div className="flex items-start gap-1">
                          {isMaster && !isMine && (
                            <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 mt-1" onClick={() => { setMessageToDelete(msg.id); setDeleteDialogOpen(true); }}>
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          )}
                          <div className={`max-w-[75%] rounded-lg px-3 py-2 ${isMine ? 'bg-blue-500 text-white' : 'bg-white border border-slate-200'}`}>
                            <p className={`text-xs font-semibold mb-1 ${isMine ? 'text-blue-100' : 'text-slate-600'}`}>{msg.from_user}</p>
                            <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                            <p className={`text-xs mt-1 ${isMine ? 'text-blue-200' : 'text-slate-400'}`}>
                              {new Date(msg.created_at).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          {isMaster && isMine && (
                            <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 mt-1" onClick={() => { setMessageToDelete(msg.id); setDeleteDialogOpen(true); }}>
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
            </TabsContent>

            <TabsContent value="group" className="flex-1 flex flex-col mt-0 h-0">
              <div className="h-full overflow-y-auto p-4 space-y-3 bg-slate-50 scrollbar-chat">
                {groupMessages.length === 0 ? (
                  <div className="text-center text-slate-500 py-8">No hay mensajes grupales</div>
                ) : (
                  groupMessages.map((msg) => {
                    const isMine = msg.from_user === user.username;
                    return (
                      <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'} group`}>
                        <div className="flex items-start gap-1">
                          {isMaster && !isMine && (
                            <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 mt-1" onClick={() => { setMessageToDelete(msg.id); setDeleteDialogOpen(true); }}>
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          )}
                          <div className={`max-w-[75%] rounded-lg px-3 py-2 ${isMine ? 'bg-green-500 text-white' : 'bg-white border border-slate-200'}`}>
                            <p className={`text-xs font-semibold mb-1 ${isMine ? 'text-green-100' : 'text-slate-600'}`}>{msg.from_user}</p>
                            <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                            <p className={`text-xs mt-1 ${isMine ? 'text-green-200' : 'text-slate-400'}`}>
                              {new Date(msg.created_at).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          {isMaster && isMine && (
                            <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 mt-1" onClick={() => { setMessageToDelete(msg.id); setDeleteDialogOpen(true); }}>
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
            </TabsContent>
          </Tabs>

          <div className="p-3 border-t border-slate-200 bg-white relative">
            {showEmojiPicker && (
              <div ref={emojiPickerRef} className="absolute bottom-16 left-3 z-50">
                <EmojiPicker onEmojiClick={onEmojiClick} width={300} height={400} searchPlaceHolder="Buscar emoji..." previewConfig={{ showPreview: false }} />
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="ghost" size="icon" onClick={() => setShowEmojiPicker(!showEmojiPicker)} className="text-slate-600 hover:bg-slate-100" data-testid="chat-emoji-button">
                <Smile className="w-5 h-5" />
              </Button>
              <Input
                ref={inputRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                data-testid="chat-message-input"
                placeholder={activeTab === "group" ? "Mensaje grupal..." : selectedRecipient ? `Mensaje a ${selectedRecipient.emoji} ${selectedRecipient.name}...` : "Selecciona un destinatario..."}
                className="flex-1"
                disabled={activeTab === "direct" && !selectedRecipient}
              />
              <Button onClick={sendMessage} size="icon" className="bg-blue-500 hover:bg-blue-600" disabled={activeTab === "direct" && !selectedRecipient} data-testid="chat-send-button">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar mensaje?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer. El mensaje será eliminado permanentemente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteMessage(messageToDelete)} className="bg-red-500 hover:bg-red-600">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteConversationDialogOpen} onOpenChange={setDeleteConversationDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar toda la conversación?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer. Todos los mensajes de esta conversación serán eliminados permanentemente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConversation} className="bg-red-500 hover:bg-red-600">Eliminar Todo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
